import { useState, useRef, useEffect } from 'react';
import { Plus, Minus, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: 'Preciso instalar placas para economizar energia?',
    answer: 'Não. A solução funciona por geração compartilhada, sem obras ou alterações no imóvel. Você economiza na conta de energia sem precisar instalar placas solares, fazer construção ou investir em equipamentos.',
  },
  {
    question: 'Existe algum investimento inicial?',
    answer: 'Não. O cadastro e a análise de viabilidade são gratuitos. Basta enviar a sua última fatura de energia para que um especialista avalie a possibilidade de economia na sua região.',
  },
  {
    question: 'O iGreen Club é gratuito?',
    answer: 'Sim. O benefício está disponível para clientes dos serviços participantes e licenciados, conforme as condições vigentes. Ao contratar um serviço iGreen ou se tornar um licenciado, você ganha acesso gratuito ao clube de descontos e vantagens.',
  },
  {
    question: 'Como faço para analisar minha conta?',
    answer: 'Envie o PDF da última fatura pelo WhatsApp. A unidade consumidora será analisada conforme a distribuidora e as regras vigentes. Nossos especialistas verificam a viabilidade e apresentam a proposta de economia personalizada.',
  },
];

function FAQItem({ item, index, isOpen, onToggle }: {
  item: typeof faqs[0];
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`rounded-xl border transition-all duration-500 ${
        isOpen
          ? 'border-primary-500/30 bg-primary-50/40'
          : 'border-background-200/70 bg-background-50 hover:border-background-300'
      } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
      >
        <span className={`text-sm md:text-base font-semibold leading-snug ${
          isOpen ? 'text-primary-700' : 'text-foreground-950'
        }`}>
          {item.question}
        </span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all duration-300 ${
          isOpen
            ? 'bg-primary-500 text-white rotate-0'
            : 'bg-background-100 text-foreground-500'
        }`}>
          {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </div>
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isOpen ? (contentRef.current?.scrollHeight ?? 300) + 'px' : '0px',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className="px-5 md:px-6 pb-5 md:pb-6">
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="duvidas" className="immersive-panel bg-background-50">
      <div className="immersive-inner">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Title */}
          <div className="text-center md:text-left lg:col-span-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 border border-primary-200 text-primary-700 text-sm font-semibold mb-5">
              <HelpCircle className="w-4 h-4" />
              Dúvidas frequentes
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tighter leading-[1.1]">
              Informação clara para{' '}
              <span className="text-gradient">decidir com segurança</span>
            </h2>
            <p className="mt-5 text-foreground-600 leading-relaxed">
              Tire suas principais dúvidas sobre os serviços iGreen e entenda como funciona cada solução.
            </p>
          </div>

          {/* Right: Accordion */}
          <div className="lg:col-span-8 flex flex-col gap-3 md:gap-4">
            {faqs.map((faq, i) => (
              <FAQItem
                key={faq.question}
                item={faq}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => handleToggle(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
